import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

// How long the deliberately theatrical "checking" step lasts. It doubles
// as the latency budget: a cold Netlify function plus the Supabase auth
// round trip is roughly 0.5-1.5s, so the real request finishes inside
// this window and the function needs no performance work at all.
const CHECK_MS = 3500;

const CHECK_STAGES = [
  'Checking club records...',
  'Matching against team lists...',
  'Confirming eligibility...',
];

const SALTY_OLIVE_URL = 'https://www.salty-olive.co.uk/';

// Always format in London time. toLocaleTimeString() on its own uses the
// DEVICE timezone, so a phone set to another zone would show a
// plausible-but-wrong time on the voucher.
const timeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const dateFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

interface Voucher {
  name: string;
  refCode: string;
  issuedAtMs: number;
  expiresAtMs: number;
  // Server time at issue, corrected for half the round trip.
  serverNowMs: number;
  // performance.now() reading taken at the same moment as serverNowMs.
  anchor: number;
  // Belt-and-braces expiry using the device clock. performance.now()
  // should keep advancing while a phone is locked, but if it were ever
  // suspended the anchored clock would lag and the voucher would outlive
  // its real expiry. Expiring on whichever deadline arrives first closes
  // that off - and a device clock wound forwards can only ever expire a
  // voucher early, never extend one.
  wallDeadline: number;
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const Discounts: React.FC = () => {
  const [name, setName] = useState('');
  const [checking, setChecking] = useState(false);
  const [stage, setStage] = useState(0);
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [expired, setExpired] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const voucherRef = useRef<HTMLDivElement | null>(null);

  // Cycle the checking messages.
  useEffect(() => {
    if (!checking) return;
    const id = window.setInterval(
      () => setStage((s) => Math.min(s + 1, CHECK_STAGES.length - 1)),
      CHECK_MS / CHECK_STAGES.length
    );
    return () => window.clearInterval(id);
  }, [checking]);

  // The live clock. Two things make it trustworthy:
  //
  // 1. It is anchored on performance.now(), not Date.now() + offset. With
  //    a Date.now() offset, changing the device clock after issue shifts
  //    the time AND the countdown by the same amount, so the fake stays
  //    self-consistent. performance.now() is immune to clock changes and
  //    to an NTP re-sync mid-session.
  // 2. It ticks at 250ms and recomputes from the absolute base each time.
  //    A 1s interval drifts against the true second boundary and visibly
  //    skips or repeats seconds - exactly the artifact that makes a clock
  //    look fake. Recomputing also means a throttled background tab jumps
  //    straight to the right value when it wakes.
  useEffect(() => {
    if (!voucher || expired) return;

    const tick = () => {
      const elapsed = performance.now() - voucher.anchor;
      const current = voucher.serverNowMs + elapsed;
      setNow(current);
      if (current >= voucher.expiresAtMs || Date.now() >= voucher.wallDeadline) {
        setExpired(true);
      }
    };

    tick();
    const id = window.setInterval(tick, 250);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [voucher, expired]);

  const reset = useCallback(() => {
    setVoucher(null);
    setExpired(false);
    setError(null);
    setStage(0);
  }, []);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setChecking(false);
    setStage(0);
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setStage(0);
    setChecking(true);

    const controller = new AbortController();
    abortRef.current = controller;

    // Measure the round trip so serverNow can be corrected for latency.
    const sentAt = performance.now();
    const request = fetch('/.netlify/functions/discount-voucher', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
      signal: controller.signal,
    });
    const wait = new Promise((resolve) => window.setTimeout(resolve, CHECK_MS));

    try {
      const [response] = await Promise.all([request, wait]);
      const receivedAt = performance.now();

      if (controller.signal.aborted) return;

      if (!response.ok) {
        throw new Error('Something went wrong at our end. Please try again shortly.');
      }

      const data = await response.json();

      if (!data.ok) {
        setFailures((f) => f + 1);
        setError(
          data.rateLimited
            ? "You've requested a lot of vouchers today. Please try again tomorrow."
            : "We couldn't match that name. Please enter your full name as the club knows it."
        );
        setChecking(false);
        return;
      }

      setVoucher({
        name: data.name,
        refCode: data.refCode,
        issuedAtMs: Date.parse(data.issuedAt),
        expiresAtMs: Date.parse(data.expiresAt),
        serverNowMs: Date.parse(data.serverNow) + (receivedAt - sentAt) / 2,
        anchor: receivedAt,
        wallDeadline:
          Date.now() + (Date.parse(data.expiresAt) - Date.parse(data.serverNow)),
      });
      setFailures(0);
      setExpired(false);
      setChecking(false);
    } catch (err: any) {
      // Cancelling is a normal outcome, not an error worth showing.
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      setError(err?.message || 'Something went wrong. Please try again shortly.');
      setChecking(false);
    } finally {
      abortRef.current = null;
    }
  };

  // Move focus to the voucher on reveal, or focus is stranded on a button
  // that no longer exists.
  useEffect(() => {
    if (voucher && !expired) voucherRef.current?.focus();
  }, [voucher, expired]);

  const head = (
    <Helmet>
      <title>Club Discounts | Ashridge Park FC</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (voucher && !expired) {
    const remaining = voucher.expiresAtMs - now;
    const urgent = remaining < 5 * 60 * 1000;

    return (
      <>
        {head}
        <Container className="p-3 my-4" style={{ maxWidth: 520 }}>
          <div
            className="voucher-card"
            ref={voucherRef}
            tabIndex={-1}
            role="status"
            aria-label={`Discount voucher for ${voucher.name}`}
          >
            <p className="voucher-eyebrow">Ashridge Park FC &middot; Salty Olive</p>
            <h1 className="voucher-name">{voucher.name}</h1>
            <p className="voucher-offer">10% DISCOUNT</p>

            <p className="voucher-date">{dateFormat.format(new Date(now))}</p>
            <p className="voucher-clock" aria-hidden="true">
              {timeFormat.format(new Date(now))}
            </p>
            <p className="visually-hidden">
              Issued at {timeFormat.format(new Date(voucher.issuedAtMs))}, valid for{' '}
              {Math.ceil(remaining / 60000)} more minutes.
            </p>

            <p className="voucher-meta">Ref {voucher.refCode}</p>
            <p className={`voucher-countdown${urgent ? ' voucher-countdown-urgent' : ''}`}>
              {urgent ? 'Expiring soon — ' : ''}Valid for another{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCountdown(remaining)}
              </span>
            </p>

            <p className="voucher-meta">
              This discount cannot be used in conjunction with any offers (such as Happy Hour).
            </p>
          </div>



          <p className="text-muted small mt-3 mb-0 text-center">
            Show this screen to staff. The clock is live, so a screenshot won't work.
          </p>
          <div className="text-center mt-3">
            <Button variant="outline-primary" size="sm" onClick={reset}>
              Done
            </Button>
          </div>
        </Container>
      </>
    );
  }

  if (voucher && expired) {
    return (
      <>
        {head}
        <Container className="p-3 my-4" style={{ maxWidth: 520 }}>
          <Card className="text-center">
            <Card.Body>
              <Card.Title as="h1" className="h4">
                This voucher has expired
              </Card.Title>
              <Card.Text className="text-muted">
                Vouchers are valid for a short time so they can't be shared around.
              </Card.Text>
              <Button variant="primary" onClick={reset}>
                Get a new one
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      {head}
      <Container className="p-3 my-4" style={{ maxWidth: 520 }}>
        <h1 className="mb-4 page-title-below-logo">Club Discounts</h1>

        <Card>
          <Card.Body aria-busy={checking}>
            <Card.Text>
              <a href={SALTY_OLIVE_URL} target="_blank" rel="noopener noreferrer">
                Salty Olive
              </a>{' '}
              sponsor Ashridge Park FC, and offer <strong>10% off</strong> to people
              connected with the club &mdash; coaches, committee members and parents.
            </Card.Text>
            <Card.Text className="text-muted">
              Enter your name to get your voucher. You must be a coach, committee member
              or parent associated with Ashridge Park FC to use this discount. And this discount cannot be used in conjunction with any offers (such as Happy Hour).
            </Card.Text>
            <Card.Text className="text-muted small">
              Every request is recorded &mdash; the name entered, the date and time, and a
              hashed identifier for your connection &mdash; and checked against club
              records. See our <Link to="/privacy-policy">privacy policy</Link>.
            </Card.Text>

            {checking ? (
              <div className="text-center py-4" role="status" aria-live="polite">
                <Spinner animation="border" className="mb-3 voucher-spinner">
                  <span className="visually-hidden">{CHECK_STAGES[stage]}</span>
                </Spinner>
                <p className="mb-3">{CHECK_STAGES[stage]}</p>
                <Button variant="outline-secondary" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                {error && (
                  <Alert variant="warning">
                    {error}
                    {failures >= 2 && (
                      <div className="mt-2">
                        <span className="small">Still not working? Let us know and we'll sort it out.</span>
                        <div className="mt-2">
                          <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => setShowContact(true)}
                          >
                            Contact us
                          </Button>
                        </div>
                      </div>
                    )}
                  </Alert>
                )}
                <Form.Floating className="mb-3">
                  <Form.Control
                    id="discount-name"
                    type="text"
                    placeholder="Your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                    autoFocus
                  />
                  <label htmlFor="discount-name">Your full name</label>
                </Form.Floating>
                <div className="d-grid">
                  <Button type="submit" variant="primary" disabled={!name.trim()}>
                    Get my voucher
                  </Button>
                </div>
              </Form>
            )}
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={showContact}
        onHide={() => setShowContact(false)}
        centered
        data-bs-theme="dark"
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--navy)', color: 'white' }} />
        <Modal.Body style={{ backgroundColor: 'var(--navy-500)', color: 'white' }}>
          <ContactForm />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Discounts;
